const AppDiagnostics = require('utils/app-diagnostics');
const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { useDispatch } = require('react-redux');

const { default: IconButton } = require('@mui/material/IconButton');
const { default: Drawer } = require('@mui/material/Drawer');
const { default: List } = require('@mui/material/List');
const { default: Divider } = require('@mui/material/Divider');
const { default: Tooltip } = require('@mui/material/Tooltip');

const { default: CloseIcon } = require('@mui/icons-material/Close');
const { default: AddCircleOutlineIcon } = require('@mui/icons-material/AddCircleOutline');
const { default: ShieldOutlined } = require('@mui/icons-material/ShieldOutlined');
const { default: VisibilityIcon } = require('@mui/icons-material/Visibility');
const { default: EditIcon } = require('@mui/icons-material/Edit');
const { default: FavoriteIcon } = require('@mui/icons-material/Favorite');
const { default: SettingsIcon } = require('@mui/icons-material/Settings');
const { default: AssignmentIcon } = require('@mui/icons-material/Assignment');
const { default: HelpOutlineIcon } = require('@mui/icons-material/HelpOutline');
const { default: LockIcon } = require('@mui/icons-material/Lock');
const { default: LogoutIcon } = require('@mui/icons-material/Logout');
const { default: NotificationAddIcon } = require('@mui/icons-material/NotificationAdd');
const { default: ChecklistIcon } = require('@mui/icons-material/Checklist');
const { default: NoteAlterIcon } = require('@mui/icons-material/NoteAlt');
const { default: InfoIcon } = require('@mui/icons-material/Info');
const { default: HomeIcon } = require('@mui/icons-material/Home');
const { default: BarChartIcon } = require('@mui/icons-material/BarChart');
const { default: StarIcon } = require('@mui/icons-material/Star');
const { default: BugIcon } = require('@mui/icons-material/BugReport');
const { default: LocalActivityIcon } = require('@mui/icons-material/LocalActivity');
const { default: MenuBookIcon } = require('@mui/icons-material/MenuBook');
const { default: ImportExportIcon } = require('@mui/icons-material/ImportExport');
const { default: EditNoteIcon } = require('@mui/icons-material/EditNote');

const NearpeerLogo = require('static/nearpeer_logo_white.svg');
const NerpyIconAsset = require('static/nerpy.png');
const NerpyIconSrc = NerpyIconAsset.default || NerpyIconAsset;
const SchoolTile = require('components/SchoolTile');
const TooltipIconAnchor = require('components/TooltipIconAnchor');
const { MAIN_HEADER_HEIGHT, QUICK_SWITCH_MENU_SCHOOL_TILE_SIZE, USER_ROLE_IDS } = require('utils/constants');
const { nearpeerTheme: NearpeerTheme } = require('styles/theme');
const { context } = require('app-context');
const { alphaSort } = require('../utils/arrays');
const { useAppSelector } = require('utils/redux');
const { analyticsTemplates } = require('utils/analytics');
const { getDevice, isIOS: getIsIOS, isAndroid: getIsAndroid } = require('utils/device');
const { reloadPage } = require('utils/reload');
const { startRouteRecoverySimulation } = require('utils/route-error-recovery');
const {
    startCordovaOfflineSimulation,
    startRequestFailureSimulation,
    stopFailureSimulation
} = require('utils/request-failure-simulation');
const {
    startTwilioRecoverySimulation,
    stopTwilioRecoverySimulation
} = require('utils/twilio-recovery-debug');
const { canAccessAdmin } = require('utils/admin-access');

const AppMenuItem = require('./AppMenuItem');

const {
    actions,
    selectors: {
        all: selectors
    }
} = context;

const {
    useState,
    useRef
} = React;

const MENU_WIDTH = 320;
const TILES_CONTAINER_WIDTH = 80;
const TILE_BORDER_RADIUS = 10;
const TILE_SIZE = 55;

const internals = {};

const isCordovaApp = !!window.cordova;

const InAppBrowser = isCordovaApp ? window.cordova.InAppBrowser : null;

/*
@np-doc
id: frontend.docs.internal-email-menu-check
title: Internal email menu visibility check
section: Platform
order: 40
summary: Checks for nearpeer.com or nearpeer.net domain in emails for internal access paths.
tags: docs,auth,menu
*/
const isInternalNearpeerEmail = (email) => {

    const normalizedEmail = String(email || '').trim().toLowerCase();
    const emailParts = normalizedEmail.split('@');

    if (emailParts.length !== 2) {
        return false;
    }

    return emailParts[1] === 'nearpeer.net' || emailParts[1] === 'nearpeer.com';
};
/* @np-doc-end */

const filterOnDisplayCondition = (items) => {

    return items
        .filter(({ displayCondition }) => {

            if (typeof displayCondition === 'undefined') {
                return true;
            }

            return !!displayCondition;
        })
        .map(({ displayCondition, ...rest }) => rest);
};

const StyledIconButton = Styled(IconButton)`
    &:focus-visible {
        background-color: rgba(255, 255, 255, 0.5);
    }
`;

module.exports = function AppMenu(props) {

    const [isOpen, setIsOpen] = useState(false);
    const anchorRef = useRef(null);
    const schools = useAppSelector(selectors.getSchools);
    const dispatch = useDispatch();

    const handleIconClick = (event) => {

        setIsOpen((prevOpen) => !prevOpen);
    };

    const closeMainMenu = () => {

        setIsOpen(false);
    };

    const {
        Icon,
        push,
        items,
        currentUser,
        role,
        rolePermissions,
        canJoinNearpeerCommunity,
        switchableCommunities = [],
        joinableCommunities = [],
        rootSchool,
        currentSchool,
        isConfirmPage,
        onConfirmPageClick,
        switchSchool,
        otherCommunityUnreadCounts = {},
        schoolSlug,
        hasResourcesPage,
        resourcesUrl,
        isSSOSchool,
        isReviewEnabled,
        onLeaveReview,
        onLogout,
        debug = {},
        setDebug,
        hasOffers = false
    } = props;

    const {
        ListWrapper,
        Header,
        StyledList,
        LogoImg,
        SchoolTilesAndListWrapper,
        MenuLeft,
        SchoolTiles,
        TilesContainer,
        TileSectionDivider,
        AddNewSchoolTileWrapper,
        AddNewSchoolTile
    } = internals;

    if (!rootSchool) {
        return null;
    }

    // NOTE: We decided to hide the fast switcher if a user can't visit Nearpeer.
    const shouldShowSchoolShortcutList = !isConfirmPage && !!switchableCommunities.length;

    const schoolsByType = {};

    switchableCommunities.forEach((school) => {

        const { id, type, isCurrentSchool } = school;

        if (id === 1 || isCurrentSchool) {
            return;
        }

        schoolsByType[type] = schoolsByType[type] || [];

        schoolsByType[type].push(school);
    });

    const sortedTypes = Object.keys(schoolsByType)
        .sort(alphaSort());


    const logAnalytics = (msg) => {

        analyticsTemplates.buttons('click main menu item', msg);
    };

    const device = getDevice();
    const isIOS = getIsIOS(device);
    const isAndroid = getIsAndroid(device);
    const isMobile = isIOS || isAndroid;

    const shouldShowDebug = isInternalNearpeerEmail(currentUser?.email);
    const canCurrentUserAccessAdmin = canAccessAdmin({ currentUser, role });
    // const shouldShowInternalDocs = isInternalNearpeerEmail(currentUser?.email);

    const openInternalDocs = async () => {

        logAnalytics('main: click main menu internal docs');

        try {
            const response = await context.api.nearpeer.docs.createSession();

            const docsUrl = new URL(
                response.location || '/docs',
                context.configuration.nearpeerApi.rootBaseUrl()
            );

            window.location.assign(docsUrl.toString());
        }
        catch (err) {
            dispatch(actions.alerts.notification.push({
                message: 'Could not open Nearpeer docs.'
            }));
        }
    };

    const notifySimulation = (started, message) => {

        dispatch(actions.alerts.notification.push({
            message: started ? message : 'Failure simulation could not start in this environment.'
        }));
    };

    const startRequestSimulation = (scenario, message) => {

        const started = startRequestFailureSimulation({
            http: context.http.nearpeer,
            scenario
        });

        notifySimulation(started, started ? `${message} Running a /ping probe now.` : message);

        if (!started) {
            return;
        }

        context.api.nearpeer.ping().then(
            () => dispatch(actions.alerts.notification.push({
                message: 'Failure simulation recovered through the automatic retry.'
            })),
            (error) => dispatch(actions.alerts.notification.push({
                message: `Failure simulation ended with ${error?.response?.status || error?.code || 'an error'}. The recovery path returned the final result.`
            }))
        );
    };

    const startTwilioSimulation = (scenario, message) => {

        const started = startTwilioRecoverySimulation({ scenario });

        notifySimulation(
            started,
            `${message} Open a different group chat or DM. Watch the notifications and [twilio-recovery] console logs.`
        );
    };

    const getAdminConsoleSubMenuItems = () => {

        const subMenuArr = [];

        if (rolePermissions) {
            if (shouldShowDebug) {
                subMenuArr.push({
                    name: 'Debug',
                    items: [
                        {
                            name: 'Discover Matching',
                            isCheckbox: true,
                            isChecked: debug.discoverMatching,
                            onCheck: (val) => setDebug({ discoverMatching: val })
                        },
                        {
                            name: 'Group Sorting',
                            isCheckbox: true,
                            isChecked: debug.groupSorting,
                            onCheck: (val) => setDebug({ groupSorting: val })
                        },
                        {
                            name: 'App diagnostics',
                            isCheckbox: true,
                            isChecked: debug.appDiagnostics === true,
                            onCheck: (val) => {

                                setDebug({ appDiagnostics: val });
                                AppDiagnostics.setEnabled(val);
                            }
                        },
                        {
                            name: 'Image empty states',
                            description: 'Show profile-photo placeholders. Turn off to restore photos.',
                            isCheckbox: true,
                            isChecked: debug.imageEmptyStates === true,
                            onCheck: (val) => setDebug({ imageEmptyStates: val })
                        },
                        {
                            name: 'Chat',
                            isCheckbox: true,
                            isChecked: debug.chat,
                            onCheck: (val) => setDebug({ chat: val })
                        },
                        {
                            name: 'Failure simulations',
                            items: [
                                {
                                    name: 'Internal server error',
                                    description: 'Fail a probe with 500, then verify its automatic retry.',
                                    onClick: () => startRequestSimulation(
                                        'internal-server-error',
                                        'Internal server error simulation armed.'
                                    )
                                },
                                {
                                    name: 'Intermittent 503',
                                    description: 'Fail a probe once, then verify its automatic retry.',
                                    onClick: () => startRequestSimulation(
                                        'intermittent-503',
                                        'Intermittent 503 simulation armed.'
                                    )
                                },
                                {
                                    name: 'Heavy traffic 503',
                                    description: 'Fail the next request and its automatic retry.',
                                    onClick: () => startRequestSimulation(
                                        'heavy-traffic',
                                        'Heavy traffic simulation armed for the next request and retry.'
                                    )
                                },
                                {
                                    name: 'Gateway failure',
                                    description: 'Return 502, then 504, on recoverable requests.',
                                    onClick: () => startRequestSimulation(
                                        'gateway-failure',
                                        'Gateway failure simulation armed for the next 2 recoverable requests.'
                                    )
                                },
                                {
                                    name: 'Maintenance mode',
                                    description: 'Return one maintenance-mode 503 response.',
                                    onClick: () => startRequestSimulation(
                                        'maintenance',
                                        'Maintenance simulation armed for the next recoverable request.'
                                    )
                                },
                                {
                                    name: 'Transport abort',
                                    description: 'Abort the next request and its automatic retry.',
                                    onClick: () => startRequestSimulation(
                                        'transport-abort',
                                        'Transport abort simulation armed for the next request and retry.'
                                    )
                                },
                                {
                                    name: 'Twilio intermittent failure',
                                    description: 'Fail the next chat message load once, then verify automatic recovery.',
                                    onClick: () => startTwilioSimulation(
                                        'intermittent-failure',
                                        'Twilio intermittent failure simulation armed.'
                                    )
                                },
                                {
                                    name: 'Twilio socket stall',
                                    description: 'Stall the next chat load until its 15-second timeout, then recover.',
                                    onClick: () => startTwilioSimulation(
                                        'socket-stall',
                                        'Twilio socket stall simulation armed.'
                                    )
                                },
                                {
                                    name: 'Twilio persistent failure',
                                    description: 'Fail both automatic attempts, then verify the Retry button.',
                                    onClick: () => startTwilioSimulation(
                                        'persistent-failure',
                                        'Twilio persistent failure simulation armed.'
                                    )
                                },
                                {
                                    name: 'Cordova reports offline',
                                    description: 'Report no device connection for 10 seconds.',
                                    onClick: () => notifySimulation(
                                        startCordovaOfflineSimulation({ http: context.http.nearpeer }),
                                        'Cordova offline simulation started for 10 seconds.'
                                    )
                                },
                                {
                                    name: 'Test Route Recovery',
                                    description: 'Reload twice, then show the recovery screen.',
                                    onClick: () => {
                                        if (!startRouteRecoverySimulation()) {
                                            dispatch(actions.alerts.notification.push({
                                                message: 'Route recovery test could not start because session storage is unavailable.'
                                            }));
                                            return;
                                        }

                                        reloadPage();
                                    }
                                },
                                {
                                    name: 'Stop simulation',
                                    description: 'Restore normal request, Twilio, and Cordova connectivity behavior.',
                                    onClick: () => {
                                        const requestSimulationStopped = stopFailureSimulation();
                                        const twilioSimulationStopped = stopTwilioRecoverySimulation();

                                        dispatch(actions.alerts.notification.push({
                                            message: requestSimulationStopped || twilioSimulationStopped
                                            ? 'Failure simulation stopped.'
                                            : 'No failure simulation is active.'
                                        }));
                                    }
                                }
                            ]
                        }
                    ],
                    Icon: BugIcon
                });

            }

            if (shouldShowDebug) {
                subMenuArr.push({
                    name: 'Manage Offers',
                    link: '/app/admin/offers',
                    Icon: LocalActivityIcon
                });
                subMenuArr.push({
                    name: 'Manage Resources',
                    link: '/app/admin/resources',
                    Icon: MenuBookIcon
                });
            }

            if (!isMobile && rolePermissions.canViewDashboard) {
                subMenuArr.push({
                    name: 'Dashboard',
                    link: '/app/admin/dashboard',
                    onClick: () => logAnalytics('main: click main menu admin dashboard'),
                    Icon: BarChartIcon
                });
            }

            if (rolePermissions.canBatchNotify) {
                subMenuArr.push({
                    name: 'Send Notification',
                    link: '/app/admin/batch-notify-users',
                    onClick: () => logAnalytics('main: click main menu send notification'),
                    Icon: NotificationAddIcon
                });
            }

            if (rolePermissions.canPreapprove) {
                subMenuArr.push({
                    name: 'Preapprove Users',
                    link: '/app/admin/preapprove-users',
                    onClick: () => logAnalytics('main: click main menu preapprove users'),
                    Icon: ChecklistIcon
                });
            }

            if (rolePermissions.canManageWelcomePage) {
                subMenuArr.push({
                    name: 'Manage Homepage',
                    link: '/app/admin/welcome-page',
                    onClick: () => logAnalytics('main: click main menu manage homepage'),
                    Icon: EditNoteIcon
                });
            }

            if (rolePermissions.canCreateSurveys) {
                subMenuArr.push({
                    name: 'Survey Management',
                    link: '/app/surveys/management',
                    onClick: () => logAnalytics('main: click main menu manage surveys'),
                    Icon: NoteAlterIcon
                });
            }
        }

        return subMenuArr;
    };

    const buildAppMenuItems = () => {

        const adminConsoleSubMenuItems = getAdminConsoleSubMenuItems();
        const viewProfileLink = `/app/profile/${currentUser ? currentUser.id : ''}`;
        const shouldShowCommunityManagement = !!switchableCommunities.length || !!joinableCommunities.length;
        const hasNewCommunityInvites = !!joinableCommunities.find(({ isNewInvite }) => isNewInvite);
        const otherCommunityUnreadCountsTotal = Object.values(otherCommunityUnreadCounts || {})
            .reduce((total, count) => total + count, 0);

        return filterOnDisplayCondition([
            // {
            //     displayCondition: shouldShowInternalDocs,
            //     name: 'Nearpeer Docs',
            //     onClick: openInternalDocs,
            //     Icon: NerpyIconSrc
            // },
            {
                displayCondition: canCurrentUserAccessAdmin && (rolePermissions?.canBatchNotify || rolePermissions?.canPreapprove || rolePermissions?.canManageWelcomePage),
                name: 'Admin Console',
                items: adminConsoleSubMenuItems,
                Icon: ShieldOutlined
            },
            {
                name: 'Home Page',
                link: '/app/welcome',
                Icon: HomeIcon
            },
            {
                name: 'View Profile',
                link: viewProfileLink,
                Icon: VisibilityIcon
            },
            {
                name: 'Edit Profile',
                link: '/app/profile/edit',
                Icon: EditIcon
            },
            {
                name: 'Edit Interests',
                link: '/app/profile/edit/interests',
                Icon: FavoriteIcon
            },
            {
                displayCondition: !!currentSchool?.offersEnabled && hasOffers && (
                    USER_ROLE_IDS.STUDENT.includes(currentUser?.roleId) ||
                    (USER_ROLE_IDS.SUPERUSER.includes(currentUser?.roleId) && isInternalNearpeerEmail(currentUser?.email))
                ),
                name: 'My Offers',
                link: '/app/offers',
                Icon: LocalActivityIcon
            },
            {
                displayCondition: USER_ROLE_IDS.PROSPECT.includes(currentUser?.roleId),
                name: 'Resources',
                link: '/app/resources',
                Icon: MenuBookIcon
            },
            {
                name: 'Preferences',
                link: '/app/preferences',
                Icon: SettingsIcon
            },
            {
                name: 'My Surveys',
                link: '/app/surveys/my-surveys',
                Icon: AssignmentIcon
            },
            {
                displayCondition: !isSSOSchool,
                name: 'Change Password',
                link: '/app/profile/change-password',
                Icon: LockIcon
            },
            {
                displayCondition: !!schoolSlug && !!hasResourcesPage,
                name: 'Community Resources',
                Icon: InfoIcon,
                onClick: () => {

                    const url = resourcesUrl || `https://nearpeer.com/resources/${schoolSlug}`;

                    analyticsTemplates.navigationWithVal(
                        'viewed resources page',
                        'main: viewed resources page',
                        url
                    );

                    if (isCordovaApp && InAppBrowser) {
                        InAppBrowser.open(url, '_system');
                    }
                    else {
                        window.open(url, '_blank');
                    }
                }
            },
            {
                displayCondition: !!shouldShowCommunityManagement,
                name: 'Manage Communities',
                link: '/app/manage-communities',
                badgeNumber: hasNewCommunityInvites ? 'NEW' : otherCommunityUnreadCountsTotal,
                Icon: ImportExportIcon
            },
            {
                displayCondition: isMobile && isReviewEnabled,
                name: 'Rate Nearpeer',
                onClick: () => {

                    logAnalytics('main: click main menu leave a review');
                    onLeaveReview();
                },
                Icon: StarIcon
            },
            {
                name: 'Support',
                link: '/app/bug-report',
                Icon: HelpOutlineIcon
            },
            { isDivider: true, name: 'logoutDivider' },
            {
                name: 'Log Out',
                onClick: onLogout,
                Icon: LogoutIcon
            }
        ]);
    };

    const menuItems = items || buildAppMenuItems();

    return (
        <React.Fragment>
            <Tooltip
                arrow
                title={isConfirmPage ? '' : 'Main Menu'}
                placement='bottom'
            >
                <TooltipIconAnchor>
                    <StyledIconButton
                        id='main-menu-button'
                        ref={anchorRef}
                        aria-label='Main menu'
                        aria-controls='app-menu'
                        aria-expanded={isOpen}
                        onClick={isConfirmPage ? onConfirmPageClick : handleIconClick}
                        size='large'
                    >
                        {Icon}
                    </StyledIconButton>
                </TooltipIconAnchor>
            </Tooltip>
            <Drawer
                open={isOpen}
                onClose={closeMainMenu}
                PaperProps={{
                    sx: {
                        backgroundColor: '#000000',
                        paddingTop: 'var(--safezonetop)',
                        paddingBottom: 'var(--safezonebottom)'
                    }
                }}
            >
                <ListWrapper $shouldShowSchoolShortcutList={shouldShowSchoolShortcutList}>
                    <Header>
                        <LogoImg src={NearpeerLogo} alt='Nearpeer logo' />
                        <StyledIconButton
                            onClick={closeMainMenu}
                            aria-label='Close main menu'
                        >
                            <CloseIcon />
                        </StyledIconButton>
                    </Header>
                    <SchoolTilesAndListWrapper>
                        {shouldShowSchoolShortcutList && (
                            <MenuLeft>
                                <SchoolTiles>
                                    {/* Always show current school at top */}
                                    <TilesContainer>
                                        <SchoolTile
                                            schools={schools}
                                            key={currentSchool.id}
                                            isCurrentSchool
                                            slug={currentSchool.slug}
                                            useNearpeerTheme
                                            showTooltip
                                            tileSize={QUICK_SWITCH_MENU_SCHOOL_TILE_SIZE}
                                        />
                                    </TilesContainer>
                                    {/* Always show root Nearpeer school after current */}
                                    {canJoinNearpeerCommunity && !rootSchool.isCurrentSchool && (
                                        <>
                                            <TileSectionDivider />
                                            <TilesContainer>
                                                <SchoolTile
                                                    schools={schools}
                                                    key={rootSchool.id}
                                                    slug={rootSchool.slug}
                                                    useNearpeerTheme
                                                    showTooltip
                                                    tileSize={QUICK_SWITCH_MENU_SCHOOL_TILE_SIZE}
                                                    onClick={() => switchSchool(rootSchool)}
                                                    unreadCount={otherCommunityUnreadCounts[rootSchool.id]}
                                                />
                                            </TilesContainer>
                                        </>
                                    )}
                                    {sortedTypes.map((type) => {

                                        return (<>
                                            <TileSectionDivider />
                                            <TilesContainer>
                                                {schoolsByType[type].map((school) => {

                                                    const {
                                                        id,
                                                        slug
                                                    } = school;

                                                    return (
                                                        <SchoolTile
                                                            schools={schools}
                                                            key={id}
                                                            slug={slug}
                                                            useNearpeerTheme
                                                            showTooltip
                                                            tileSize={QUICK_SWITCH_MENU_SCHOOL_TILE_SIZE}
                                                            onClick={() => switchSchool(school)}
                                                            unreadCount={otherCommunityUnreadCounts[school.id]}
                                                        />
                                                    );
                                                })}
                                            </TilesContainer>
                                        </>);
                                    })}
                                </SchoolTiles>
                                {/* <AddNewSchoolTileWrapper>
                                    <AddNewSchoolTile
                                        aria-label='Manage communities'
                                        onClick={() => {

                                            push('/app/manage-communities');
                                            closeMainMenu();
                                        }}
                                    >
                                        <AddCircleOutlineIcon />
                                    </AddNewSchoolTile>
                                </AddNewSchoolTileWrapper> */}
                            </MenuLeft>
                        )}
                        <nav id='app-menu' aria-label='Main menu' style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
                            <StyledList>
                                {menuItems.map((item) => {

                                    if (item.isDivider) {
                                        return <Divider key={item.name} component='li' />;
                                    }

                                    return (
                                        <AppMenuItem
                                            disablePadding
                                            key={item.name}
                                            closeMainMenu={closeMainMenu}
                                            push={push}
                                            {...item}
                                            onClick={() => {

                                                if (item.onClick) {
                                                    item.onClick();
                                                }

                                                if (item.link) {
                                                    push(item.link);
                                                }

                                                if (!item.items) {
                                                    closeMainMenu();
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </StyledList>
                        </nav>
                    </SchoolTilesAndListWrapper>
                </ListWrapper>
            </Drawer>
        </React.Fragment>
    );
};

module.exports.propTypes = {
    id: T.string.isRequired,
    Icon: T.element,
    items: T.arrayOf(T.shape({
        name: T.string.isRequired,
        link: T.string,
        Icon: T.oneOfType([
            T.elementType,
            T.element,
            T.string
        ]),
        items: T.array,
        inset: T.bool,
        disabled: T.bool,
        onClick: T.func
    })),
    push: T.func,
    currentSchool: T.object,
    isConfirmPage: T.bool,
    onConfirmPageClick: T.func,
    rootSchool: T.object,
    switchableCommunities: T.arrayOf(T.object),
    canJoinNearpeerCommunity: T.bool,
    switchSchool: T.func,
    otherCommunityUnreadCounts: T.object,
    currentUser: T.object,
    role: T.shape({
        id: T.number,
        roleGroupId: T.number
    }),
    rolePermissions: T.object,
    joinableCommunities: T.arrayOf(T.object),
    schoolSlug: T.string,
    hasResourcesPage: T.bool,
    resourcesUrl: T.string,
    isSSOSchool: T.bool,
    isReviewEnabled: T.bool,
    onLeaveReview: T.func,
    onLogout: T.func,
    debug: T.object,
    setDebug: T.func,
    hasOffers: T.bool
};

internals.ListWrapper = Styled.div`
    width: ${({ $shouldShowSchoolShortcutList }) => $shouldShowSchoolShortcutList ? MENU_WIDTH : MENU_WIDTH - TILES_CONTAINER_WIDTH / 2}px;
    height: 100%;
    display: flex;
    flex-flow: column;
    background: white;
`;

internals.LogoImg = Styled.img`
    width: 140px;
`;

internals.Header = Styled.div`
    height: ${MAIN_HEADER_HEIGHT}px;
    min-height: ${MAIN_HEADER_HEIGHT}px;
    width: 100%;
    background-color: ${NearpeerTheme.palette.primary.main};

    display: flex;
    flex-flow: row nowrap;
    padding-left: ${({ theme }) => theme.spacing(2.5)};
    padding-right: ${({ theme }) => theme.spacing(1.5)};
    align-items: center;
    justify-content: space-between;

    svg {
        fill: white;
    }
`;

internals.StyledList = Styled(List)`
    padding: 0;
    flex: 1;
    overflow-y: auto;
`;

internals.MenuLeft = Styled.div`
    width: ${TILES_CONTAINER_WIDTH}px;
    height: 100%;

    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: space-between;

    background-color: #FFD9E6;
    border-right: 1px solid #AD97D7;
`;

internals.SchoolTiles = Styled.div`
    width: 100%;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;

    overflow-y: auto;

    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */

    &::-webkit-scrollbar {
        display: none; /* Safari and Chrome */
    }
`;

internals.TileSectionDivider = Styled.div`
    width: calc(100% - ${({ theme }) => theme.spacing(1.5)} * 2);
    min-height: 2px;
    background: #AD97D7;
`;

internals.TilesContainer = Styled.div`
    padding: ${({ theme }) => theme.spacing(1.5)} 0;

    display: flex;
    flex-flow: column nowrap;
    align-items: center;

    gap: ${({ theme }) => theme.spacing(2)};
`;

internals.AddNewSchoolTileWrapper = Styled.div`
    margin: ${({ theme }) => theme.spacing(2)};

    padding-top: ${({ theme }) => theme.spacing(1.5)};
    border-top: 2px solid #AD97D7;
`;

internals.AddNewSchoolTile = Styled(IconButton)`
    width: ${TILE_SIZE}px;
    height: ${TILE_SIZE}px;
    background: white;

    border: 2px solid #AD97D7;
    border-radius: ${TILE_BORDER_RADIUS}px;

    svg {
        width: 30px;
        height: 30px;
        fill: ${NearpeerTheme.palette.primary.main};
    }

    &:hover {
        border: 2px solid ${NearpeerTheme.palette.primary.main};
    }

    &:focus-visible {
        outline: 2px solid ${NearpeerTheme.palette.primary.main} !important;
        outline-offset: 2px;
    }
`;

internals.SchoolTilesAndListWrapper = Styled.div`
    height: 100%;
    display: flex;
    flex-flow: row nowrap;

    overflow-y: hidden;
`;
